import { skipToken, useMutation, useQueries, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { Layouts } from 'react-grid-layout'
import type { IStateReducer } from '~/types/analytics-context'
import type {
  ICompareCohortsState,
  IStateReducer as ICompareCohortsStateReducer,
} from '~/types/compare-cohorts-context'
import type {
  IGetOverallResponse,
  IGetSeriesDataBody,
  IChartConfigApiModel,
  IBuilderConfig,
  CA_PAGES,
  IGetFieldsPayload,
  BUSINESS_REPORTS_PAGES,
} from '~/types/graph-builder'
import type { IPageLayout } from '~/types/graph-layout'

import {
  createChartConfig,
  batchCreatePageLayout,
  deleteChartConfig,
  getChartConfig,
  getFields,
  getPageLayout,
  getSeriesBatchData,
  getSeriesData,
  listPageLayouts,
  listChartConfigs,
  updateChartConfig,
  updatePageLayout,
  deletePageLayout,
} from '~/api/graphs-builder'
import { Mode } from '~/types/graph-builder'

////////// KEYS //////////

export const keys = {
  all: ['graphs-builder'] as const,
  allLayouts: (page: CA_PAGES | BUSINESS_REPORTS_PAGES) => [...keys.all, 'all-page-layouts', page] as const,
  batchSeries: (params: { body: IGetSeriesDataBody[]; mode: string }) =>
    [...keys.all, 'getSeries', { mode: params.mode }, JSON.stringify(params.body)] as const,
  getOne: (configId?: string) => [...keys.all, 'getOne', configId] as const,
  list: (params: { appKey?: string; deviceType?: string; pageName?: CA_PAGES | BUSINESS_REPORTS_PAGES }) =>
    [...keys.all, 'list', params] as const,
  onlyOverall: (
    body: IGetSeriesDataBody,
    kpiField: string,
    state: IStateReducer | ICompareCohortsStateReducer,
    appId?: string
  ) => ['only-overall', appId, body, kpiField, state] as const,
  pageLayout: (page: CA_PAGES | BUSINESS_REPORTS_PAGES, section?: string) =>
    [...keys.all, 'page-layout', page, section] as const,
  series: (params: { body: IGetSeriesDataBody; mode: string }) =>
    [...keys.all, 'getSeries', { mode: params.mode }, JSON.stringify(params.body)] as const,
}

////////// QUERIES //////////

export const useGetFieldsQuery = (params: IGetFieldsPayload): UseQueryResult<string[]> => {
  return useQuery({
    queryFn: params.appKey ? () => getFields(params) : skipToken,
    queryKey: ['graph-builder-fields', params],
  })
}

export const useChartConfigsListQuery = (params: {
  appKey?: string
  deviceType?: string
  page?: number
  pageName?: CA_PAGES | BUSINESS_REPORTS_PAGES
  size?: number
}): UseQueryResult<IPaginatedResponse<IChartConfigApiModel>> => {
  return useQuery({
    queryFn: () => listChartConfigs(params),
    queryKey: keys.list(params),
  })
}

export const useGetBuilderBatchDataQuery = <T = unknown>({
  body,
  mode,
  options,
  queryFn,
}: {
  body: IGetSeriesDataBody[]
  mode: Mode
  options?: { enabled?: boolean }
  queryFn?: (body: IGetSeriesDataBody[]) => Promise<T>
}): UseQueryResult<T> => {
  return useQuery({
    enabled: options?.enabled !== false,
    queryFn: () => (queryFn ? queryFn(body) : getSeriesBatchData(body, mode)),
    queryKey: keys.batchSeries({ body, mode }),
  })
}

export const useGetCompareCohortOverallQueries = ({
  appKey,
  bodies,
  kpiField,
  options,
  queryFn,
  state,
}: {
  appKey?: string
  bodies: IGetSeriesDataBody[]
  deviceType?: string
  kpiField: string
  options?: { enabled?: boolean }
  queryFn?: (body: IGetSeriesDataBody) => Promise<any>
  state: ICompareCohortsState
}): UseQueryResult<IGetOverallResponse>[] => {
  return useQueries({
    queries: bodies.map((body, index) => {
      return {
        enabled: !!appKey && options?.enabled !== false,
        queryFn: () => (queryFn ? queryFn(body) : getSeriesData(body, Mode.OVERALL)),
        queryKey: keys.onlyOverall(body, kpiField, state[index], appKey),
      }
    }),
  })
}

export const useGetChartConfigQuery = (configId?: string): UseQueryResult<IChartConfigApiModel> => {
  return useQuery({
    queryFn: configId ? () => getChartConfig(configId) : skipToken,
    queryKey: keys.getOne(configId),
  })
}

export const useListPageLayoutsQuery = ({
  page,
}: {
  page: CA_PAGES | BUSINESS_REPORTS_PAGES
}): UseQueryResult<IPageLayout[]> => {
  return useQuery({
    queryFn: () => listPageLayouts({ page }),
    queryKey: keys.allLayouts(page),
  })
}

export const useGetPageLayoutQuery = ({
  page,
  section,
}: {
  page: CA_PAGES | BUSINESS_REPORTS_PAGES
  section?: string
}): UseQueryResult<IPageLayout> => {
  return useQuery({
    queryFn: () => getPageLayout({ page, section }),
    queryKey: keys.pageLayout(page, section),
  })
}

////////// MUTATIONS //////////

export const useCreateChartConfigMutation = (): UseMutationResult<
  IChartConfigApiModel,
  unknown,
  IBuilderConfig,
  unknown
> => {
  return useMutation({ mutationFn: createChartConfig })
}

export const useDeleteChartConfigMutation = (): UseMutationResult<void, unknown, { configId: string }, unknown> => {
  return useMutation({ mutationFn: deleteChartConfig })
}

export const useUpdateChartConfigMutation = (): UseMutationResult<
  void,
  unknown,
  IBuilderConfig & { configId: string },
  unknown
> => {
  return useMutation({ mutationFn: updateChartConfig })
}

export const useBatchCreatePageLayoutMutation = (): UseMutationResult<
  void,
  unknown,
  { page: CA_PAGES | BUSINESS_REPORTS_PAGES; sections: { layout: Layouts; section_name?: string }[] },
  unknown
> => {
  return useMutation({ mutationFn: batchCreatePageLayout })
}

export const useUpdatePageLayoutMutation = (): UseMutationResult<
  IPageLayout,
  unknown,
  { layout: Layouts; page: CA_PAGES | BUSINESS_REPORTS_PAGES; section?: string },
  unknown
> => {
  return useMutation({ mutationFn: updatePageLayout })
}

export const useDeletePageLayoutMutation = (): UseMutationResult<
  void,
  unknown,
  { page: CA_PAGES | BUSINESS_REPORTS_PAGES; section: string },
  unknown
> => {
  return useMutation({ mutationFn: deletePageLayout })
}
